import { toPng } from "html-to-image";
import * as QRCode from "qrcode";
import "./medical-record.css";
import {
  medicalRecordByPersonaCode,
  medicalRecordByPersonaName,
  type MedicalRecord,
} from "./medical-records";

type ResultContext = {
  resultId: string;
  personaName: string;
  personaCode: string;
  bloodPressureSwitchText?: string;
  qrHtml?: string;
};

type FeedbackValue = "像我" | "有点像" | "不像";
type MedicalRecordSource = "result_card_inner" | "feedback_archived";

const STORAGE_KEY = "unit-atlas-medical-record-context";
const FEEDBACK_PREFIX = "unit-atlas-medical-record-feedback:";
const SESSION_KEY = "unit-atlas-session-id";
const EVENT_SEEN_PREFIX = "unit-atlas-medical-record-event-seen:";
const DEPTH_STATE_PREFIX = "unit-atlas-medical-record-depth:";
const MEDICAL_HASH = "#medical-record";
const PUBLIC_QUIZ_ORIGIN = "https://vibefvking.com";
const QR_VERSION = "20260625";
const LATEST_ENTRY_PATH = "/";
const root = document.querySelector<HTMLElement>("#root");
const overlayRoot = document.createElement("div");
overlayRoot.id = "medical-record-root";
overlayRoot.hidden = true;
document.body.append(overlayRoot);

let activeDepthTracker: (() => void) | null = null;
let activeSectionObserver: IntersectionObserver | null = null;

function normalizeLegacyEntry() {
  const url = new URL(window.location.href);
  let changed = false;
  const isLegacyPath = url.pathname !== "/" && url.pathname !== "/index.html";
  const hasLegacyVersion = url.searchParams.has("v") && url.searchParams.get("v") !== QR_VERSION;

  if (isLegacyPath) {
    url.pathname = LATEST_ENTRY_PATH;
    changed = true;
  }

  if (url.searchParams.get("v") !== QR_VERSION) {
    url.searchParams.set("v", QR_VERSION);
    changed = true;
  }

  if (
    !url.searchParams.get("source") &&
    !url.searchParams.get("utm_source") &&
    (isLegacyPath || hasLegacyVersion)
  ) {
    url.searchParams.set("source", "legacy_qr");
  }

  if (url.hash && !sessionStorage.getItem(STORAGE_KEY)) {
    url.hash = "";
    changed = true;
  }

  if (changed) {
    history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }
}

normalizeLegacyEntry();

function warn(message: string, detail?: unknown) {
  if (import.meta.env.DEV) console.warn(`[unit-atlas-medical-record] ${message}`, detail ?? "");
}

function trackEvent(eventName: string, payload: Record<string, unknown>) {
  const body = {
    event_name: eventName,
    page_name: String(payload.page_name ?? currentPageName()),
    payload: {
      timestamp: new Date().toISOString(),
      source_channel: sourceChannel(),
      device: deviceType(),
      path: location.pathname,
      hash: location.hash,
      ...payload,
    },
    session_id: sessionId(),
    source_channel: sourceChannel(),
    device: deviceType(),
  };

  if (import.meta.env.DEV) {
    console.log("[unit-atlas-track-local]", eventName, body.payload);
  }

  if (typeof window === "undefined") return;

  window
    .fetch("/api/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": body.session_id,
      },
      body: JSON.stringify(body),
      keepalive: true,
    })
    .catch((error) => {
      if (import.meta.env.DEV) warn("track_event_failed", { eventName, error });
    });
}

function postApi(path: string, payload: Record<string, unknown>) {
  const body = {
    session_id: sessionId(),
    source_channel: sourceChannel(),
    device: deviceType(),
    ...payload,
  };

  window
    .fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": body.session_id,
      },
      body: JSON.stringify(body),
      keepalive: true,
    })
    .catch((error) => {
      if (import.meta.env.DEV) warn("post_api_failed", { path, error });
    });
}

function sessionId() {
  const existing = window.sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const next =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (item) =>
          (Number(item) ^ (Math.random() * 16) >> (Number(item) / 4)).toString(16),
        );
  window.sessionStorage.setItem(SESSION_KEY, next);
  return next;
}

function sourceChannel() {
  const params = new URLSearchParams(location.search);
  return params.get("source") ?? params.get("utm_source") ?? "direct";
}

function deviceType() {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|iphone|android|windows phone/.test(ua)) return "mobile";
  if (/ipad|tablet/.test(ua)) return "tablet";
  return "desktop";
}

function currentPageName() {
  if (location.hash === MEDICAL_HASH) return "medical_record";
  if (document.querySelector("#result-card")) return "result";
  return "unknown";
}

function oncePerResult(resultId: string, key: string) {
  const storageKey = `${EVENT_SEEN_PREFIX}${resultId}:${key}`;
  if (sessionStorage.getItem(storageKey)) return false;
  sessionStorage.setItem(storageKey, "1");
  return true;
}

function text(selector: string, parent: ParentNode = document) {
  return parent.querySelector<HTMLElement>(selector)?.innerText.trim() ?? "";
}

function cleanQuotedText(value: string) {
  return value.replace(/^[“”"'『』「」]+|[“”"'『』「」]+$/g, "").trim();
}

function cleanShareQuote(value?: string) {
  return value?.replace(/^[“”"'『』「」（）()《》]+|[“”"'『』「」（）()《》]+$/g, "").trim();
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function quizUrl(source: string) {
  return `${PUBLIC_QUIZ_ORIGIN}/?source=${encodeURIComponent(source)}&v=${QR_VERSION}`;
}

function qrSvg(value: string, size = 92) {
  const qr = QRCode.create(value, { errorCorrectionLevel: "M" });
  const moduleCount = qr.modules.size;
  const quietZone = 2;
  const viewSize = moduleCount + quietZone * 2;
  const path = Array.from(qr.modules.data)
    .map((enabled, index) => {
      if (!enabled) return "";
      const x = (index % moduleCount) + quietZone;
      const y = Math.floor(index / moduleCount) + quietZone;
      return `M${x} ${y}h1v1H${x}z`;
    })
    .join("");

  return `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="${size}"
      height="${size}"
      viewBox="0 0 ${viewSize} ${viewSize}"
      role="img"
      aria-label="扫码测测你的单位病症"
      data-qr-value="${escapeHtml(value)}"
    >
      <path fill="#fffaf3" d="M0 0h${viewSize}v${viewSize}H0z"></path>
      <path fill="#142033" d="${path}"></path>
    </svg>
  `;
}

function buildResultContext(): { context: ResultContext; record: MedicalRecord } | null {
  const resultCard = document.querySelector<HTMLElement>("#result-card");
  if (!resultCard) return null;

  const personaName = text(".identity-copy h2", resultCard);
  const personaCode = text(".identity-copy em", resultCard).replace(/^类型代码[：:]\s*/, "");
  const resultId = text(".result-header span", resultCard).replace(/^病历编号\s*/, "");
  const bloodPressureSwitchText = cleanQuotedText(text(".pressure-card p", resultCard));
  const qrHtml = qrSvg(quizUrl("medical-record"));
  const record =
    medicalRecordByPersonaCode.get(personaCode) ?? medicalRecordByPersonaName.get(personaName);

  if (!resultId) {
    warn("未找到 result_id，病历入口不会展示。");
    return null;
  }
  if (!record) {
    warn("未找到当前人格对应的病历内容，病历入口不会展示。", {
      personaName,
      personaCode,
    });
    return null;
  }
  if (!bloodPressureSwitchText) {
    warn("未找到结果页当前展示的 bloodPressureSwitchText，病历页将隐藏血压开关模块。");
  }

  return {
    context: {
      resultId,
      personaName,
      personaCode,
      bloodPressureSwitchText: bloodPressureSwitchText || undefined,
      qrHtml: qrHtml || undefined,
    },
    record,
  };
}

function saveContext(context: ResultContext) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
}

function loadContext(): ResultContext | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ResultContext) : null;
  } catch {
    return null;
  }
}

function feedbackKey(resultId: string) {
  return `${FEEDBACK_PREFIX}${resultId}`;
}

function getFeedback(resultId: string) {
  return sessionStorage.getItem(feedbackKey(resultId)) as FeedbackValue | null;
}

function setFeedback(context: ResultContext, value: FeedbackValue) {
  sessionStorage.setItem(feedbackKey(context.resultId), value);
  postApi("/api/feedback_submit", {
    rating: null,
    message: `medical_record_feedback:${value}`,
  });
  trackEvent("feedback_submit", {
    page_name: "result",
    result_id: context.resultId,
    persona_id:
      medicalRecordByPersonaCode.get(context.personaCode)?.personaId ??
      medicalRecordByPersonaName.get(context.personaName)?.personaId,
    persona_name: context.personaName,
    feedback_value: value,
    blood_pressure_switch_text: context.bloodPressureSwitchText,
    source: "medical_record_archive",
  });
}

function openMedicalRecord(context: ResultContext, source: MedicalRecordSource) {
  saveContext(context);
  const record =
    medicalRecordByPersonaCode.get(context.personaCode) ??
    medicalRecordByPersonaName.get(context.personaName);
  if (!record) {
    warn("打开病历时未找到人格病历。", context);
    return;
  }
  trackEvent("medical_record_click", {
    page_name: "result",
    result_id: context.resultId,
    persona_id: record.personaId,
    persona_name: context.personaName,
    blood_pressure_switch_text: context.bloodPressureSwitchText,
    source,
  });
  if (location.hash !== MEDICAL_HASH) {
    history.pushState({ medicalRecord: true }, "", MEDICAL_HASH);
  }
  renderMedicalRecord(context, record);
}

function closeMedicalRecord() {
  teardownMedicalRecordAnalytics();
  overlayRoot.hidden = true;
  overlayRoot.replaceChildren();
  if (root) root.hidden = false;
  document.body.classList.remove("medical-record-open");
  if (location.hash === MEDICAL_HASH) {
    history.back();
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function enhanceResultPage() {
  const result = buildResultContext();
  if (!result) return;

  const { context } = result;
  const resultCard = document.querySelector<HTMLElement>("#result-card");
  const hiddenSection = resultCard?.querySelector<HTMLElement>(".hidden-symptom-section");
  const qrCard = resultCard?.querySelector<HTMLElement>(".result-qr-card");
  const actions = document.querySelector<HTMLElement>(".result-actions");
  if (!resultCard || !hiddenSection || !qrCard || !actions) return;

  const resultQrBox = resultCard.querySelector<HTMLElement>(".result-qr-box");
  if (resultQrBox && resultQrBox.dataset.qrVersion !== QR_VERSION) {
    resultQrBox.innerHTML = qrSvg(quizUrl("result-card"));
    resultQrBox.dataset.qrVersion = QR_VERSION;
  }

  if (!resultCard.querySelector("[data-medical-record-entry]")) {
    const entry = document.createElement("button");
    entry.type = "button";
    entry.className = "medical-record-entry";
    entry.dataset.medicalRecordEntry = "true";
    entry.innerHTML = `
      <span>病历复盘</span>
      <strong>为什么会这样？查看完整单位病历 <b aria-hidden="true">&gt;</b></strong>
    `;
    entry.addEventListener("click", () => openMedicalRecord(context, "result_card_inner"));
    hiddenSection.insertAdjacentElement("afterend", entry);
    if (oncePerResult(context.resultId, "medical_entry_impression")) {
      trackEvent("medical_record_entry_impression", {
        page_name: "result",
        result_id: context.resultId,
        persona_id: result.record.personaId,
        persona_name: context.personaName,
        persona_code: context.personaCode,
        blood_pressure_switch_text: context.bloodPressureSwitchText,
        source: "result_card_inner",
      });
    }
  }

  let feedback = document.querySelector<HTMLElement>("[data-medical-feedback]");
  if (!feedback) {
    feedback = document.createElement("section");
    feedback.className = "medical-feedback";
    feedback.dataset.medicalFeedback = "true";
    feedback.dataset.resultId = context.resultId;
    actions.insertAdjacentElement("afterend", feedback);
    renderFeedback(feedback, context);
    if (oncePerResult(context.resultId, "feedback_prompt_view")) {
      trackEvent("feedback_prompt_view", {
        page_name: "result",
        result_id: context.resultId,
        persona_id: result.record.personaId,
        persona_name: context.personaName,
        persona_code: context.personaCode,
      });
    }
  } else if (feedback.dataset.resultId !== context.resultId) {
    feedback.dataset.resultId = context.resultId;
    renderFeedback(feedback, context);
  }
}

function renderFeedback(container: HTMLElement, context: ResultContext) {
  const selected = getFeedback(context.resultId);
  if (selected) {
    container.innerHTML = `
      <p class="medical-feedback-kicker">这张像你吗？</p>
      <h3>已按「${escapeHtml(selected)}」归档</h3>
      <button type="button" class="medical-feedback-open">打开已归档病历</button>
    `;
    container
      .querySelector<HTMLButtonElement>(".medical-feedback-open")
      ?.addEventListener("click", () => openMedicalRecord(context, "feedback_archived"));
    return;
  }

  container.innerHTML = `
    <p class="medical-feedback-kicker">这张像你吗？</p>
    <h3>点一下，帮你把这份病历归档</h3>
    <div class="medical-feedback-options" role="group" aria-label="病历归档反馈">
      ${(["像我", "有点像", "不像"] as FeedbackValue[])
        .map(
          (value) =>
            `<button type="button" data-feedback-value="${value}">${value}</button>`,
        )
        .join("")}
    </div>
  `;
  container.querySelectorAll<HTMLButtonElement>("[data-feedback-value]").forEach((button) => {
    button.addEventListener("click", () => {
      const value = button.dataset.feedbackValue as FeedbackValue;
      setFeedback(context, value);
      renderFeedback(container, context);
    });
  });
}

function splitPathology(value: string) {
  const sentences = value.match(/[^。！？]+[。！？]?/g)?.map((item) => item.trim()).filter(Boolean) ?? [
    value,
  ];
  if (sentences.length < 2) return [value];
  const midpoint = Math.ceil(sentences.length / 2);
  return [sentences.slice(0, midpoint).join(""), sentences.slice(midpoint).join("")].filter(Boolean);
}

function quoteLines(value?: string) {
  const clean = cleanShareQuote(value);
  if (!clean) return [];
  const units =
    clean
      .match(/[^，；。！？]+[，；。！？]?/g)
      ?.map((item) => item.trim())
      .filter(Boolean) ?? [clean];

  if (units.length === 1 && clean.length <= 13) return [clean];

  const lines: string[] = [];
  let current = "";
  const targetLength = clean.length <= 18 ? 9 : 12;
  const maxLength = clean.length <= 18 ? 13 : 16;

  units.forEach((unit) => {
    const next = `${current}${unit}`;
    if (current && (next.length > maxLength || current.length >= targetLength)) {
      lines.push(current);
      current = unit;
      return;
    }
    current = next;
  });
  if (current) lines.push(current);

  for (let index = lines.length - 1; index > 0; index -= 1) {
    if (lines[index].length >= 4) continue;
    lines[index - 1] = `${lines[index - 1]}${lines[index]}`;
    lines.splice(index, 1);
  }

  if (lines.length > 3) {
    const compact: string[] = [];
    lines.forEach((line) => {
      const previous = compact[compact.length - 1];
      if (previous && `${previous}${line}`.length <= maxLength + 2) {
        compact[compact.length - 1] = `${previous}${line}`;
      } else {
        compact.push(line);
      }
    });
    return compact;
  }

  return lines;
}

function qrMarkup(context: ResultContext, record: MedicalRecord, className: string) {
  if (!context.qrHtml) return "";
  return `
    <section class="${className}">
      <div>
        <strong>${escapeHtml(record.qrTitle)}</strong>
        <p>${escapeHtml(record.qrSubtitle)}</p>
      </div>
      <div class="medical-qr-code">${context.qrHtml}</div>
    </section>
  `;
}

function bloodPressureMarkup(context: ResultContext) {
  if (!context.bloodPressureSwitchText) return "";
  return `
    <section class="medical-pressure">
      <span>血压开关</span>
      <p>${escapeHtml(context.bloodPressureSwitchText)}</p>
    </section>
  `;
}

function renderMedicalRecord(context: ResultContext, record: MedicalRecord) {
  if (!context.resultId) {
    warn("渲染病历页时缺少 result_id。");
    closeMedicalRecord();
    return;
  }
  if (!context.bloodPressureSwitchText) {
    warn("病历页缺少 bloodPressureSwitchText，已隐藏血压开关模块。");
  }

  const paragraphs = splitPathology(record.pathologyAnalysis);
  const quote = quoteLines(record.shareQuote);
  const hiddenLesion =
    record.companionExplanation || record.colleagueView
    ? `
      <section class="medical-hidden-lesion">
        <h2>隐藏病灶</h2>
        ${
          record.companionExplanation
            ? `<p>${escapeHtml(record.companionExplanation)}</p>`
            : ""
        }
        ${
          record.colleagueView
            ? `
              <h3 class="medical-hidden-lesion-subtitle">同事眼中的你</h3>
              <p class="medical-hidden-lesion-colleague">${escapeHtml(record.colleagueView)}</p>
            `
            : ""
        }
      </section>
    `
    : "";
  const quoteMarkup = quote.length
    ? `
      <div class="medical-quote" aria-label="病历判词">
        ${quote.map((line) => `<span>${escapeHtml(line)}</span>`).join("")}
      </div>
    `
    : "";

  overlayRoot.innerHTML = `
    <main class="medical-page">
      <article class="medical-sheet">
        <header class="medical-header">
          <div>
            <p>单位发病图鉴 · 门诊部</p>
            <span>${escapeHtml(context.resultId)}</span>
          </div>
          <strong>已归档</strong>
        </header>

        <section class="medical-diagnosis-summary">
          <div class="medical-identity">
            <span>确诊人格</span>
            <h1>${escapeHtml(record.personaName)}</h1>
            <p>${escapeHtml(record.personaCode)}</p>
          </div>
          <div class="medical-chief">
            <span>病历主诉</span>
            <h2>${escapeHtml(record.chiefComplaint)}</h2>
          </div>
        </section>

        ${bloodPressureMarkup(context)}

        <section class="medical-copy-section medical-pathology">
          <h2>病理分析</h2>
          ${paragraphs.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join("")}
        </section>

        <section class="medical-symptoms">
          <h2>典型症状</h2>
          <ol class="medical-symptom-list">
            ${[record.symptom1, record.symptom2, record.symptom3]
              .map((symptom, index) => `<li><b>${index + 1}</b><span>${escapeHtml(symptom)}</span></li>`)
              .join("")}
          </ol>
        </section>

        ${hiddenLesion}

        <section class="medical-advice">
          <h2>医嘱</h2>
          <ol>
            ${[record.advice1, record.advice2, record.advice3]
              .map((advice, index) => `<li><b>${index + 1}</b><span>${escapeHtml(advice)}</span></li>`)
              .join("")}
          </ol>
        </section>

        ${quoteMarkup}
        ${qrMarkup(context, record, "medical-qr")}
        <p class="medical-disclaimer">${escapeHtml(record.disclaimer)}</p>
      </article>

      <div class="medical-actions">
        <button type="button" class="medical-primary" data-generate-record>
          生成我的单位病历图
        </button>
        <button type="button" class="medical-secondary" data-share-record>
          发给同事会诊
        </button>
        <button type="button" class="medical-back" data-back-result>
          返回结果卡
        </button>
      </div>
    </main>
  `;

  overlayRoot.hidden = false;
  if (root) root.hidden = true;
  document.body.classList.add("medical-record-open");
  window.scrollTo({ top: 0 });

  overlayRoot
    .querySelector<HTMLButtonElement>("[data-generate-record]")
    ?.addEventListener("click", () => generateRecordImage(context, record));
  overlayRoot
    .querySelector<HTMLButtonElement>("[data-share-record]")
    ?.addEventListener("click", () => shareRecord(context, record));
  overlayRoot
    .querySelector<HTMLButtonElement>("[data-back-result]")
    ?.addEventListener("click", () => {
      trackEvent("medical_record_back_result", {
        page_name: "medical_record",
        result_id: context.resultId,
        persona_id: record.personaId,
        persona_name: record.personaName,
        persona_code: record.personaCode,
      });
      closeMedicalRecord();
    });

  initMedicalRecordAnalytics(context, record);
}

function medicalPayload(context: ResultContext, record: MedicalRecord) {
  return {
    result_id: context.resultId,
    persona_id: record.personaId,
    persona_name: record.personaName,
    persona_code: record.personaCode,
    blood_pressure_switch_text: context.bloodPressureSwitchText,
  };
}

function initMedicalRecordAnalytics(context: ResultContext, record: MedicalRecord) {
  teardownMedicalRecordAnalytics();

  const startedAt = Date.now();
  let maxDepth = 0;
  const emittedDepths = new Set<number>();
  const basePayload = medicalPayload(context, record);

  trackEvent("medical_record_view", {
    page_name: "medical_record",
    ...basePayload,
    has_blood_pressure_switch: Boolean(context.bloodPressureSwitchText),
    has_companion_explanation: Boolean(record.companionExplanation),
    has_colleague_view: Boolean(record.colleagueView),
    has_share_quote: Boolean(record.shareQuote),
    has_qr: Boolean(context.qrHtml),
  });

  const sectionTargets: Array<[string, string, string]> = [
    ["blood_pressure", ".medical-pressure", "血压开关"],
    ["pathology", ".medical-pathology", "病理分析"],
    ["symptoms", ".medical-symptoms", "典型症状"],
    ["hidden_lesion", ".medical-hidden-lesion", "隐藏病灶"],
    ["advice", ".medical-advice", "医嘱"],
    ["quote", ".medical-quote", "判词金句"],
    ["qr", ".medical-qr", "二维码"],
  ];

  activeSectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
        const target = entry.target as HTMLElement;
        const sectionId = target.dataset.analyticsSectionId;
        const sectionName = target.dataset.analyticsSectionName;
        if (!sectionId || !oncePerResult(context.resultId, `section_seen:${sectionId}`)) return;

        trackEvent("medical_record_section_seen", {
          page_name: "medical_record",
          ...basePayload,
          section_id: sectionId,
          section_name: sectionName,
        });
        activeSectionObserver?.unobserve(target);
      });
    },
    { threshold: [0.35, 0.65] },
  );

  sectionTargets.forEach(([sectionId, selector, sectionName]) => {
    const element = overlayRoot.querySelector<HTMLElement>(selector);
    if (!element) return;
    element.dataset.analyticsSectionId = sectionId;
    element.dataset.analyticsSectionName = sectionName;
    activeSectionObserver?.observe(element);
  });

  const emitDepth = (depth: number) => {
    [25, 50, 75, 90, 100].forEach((threshold) => {
      if (depth < threshold || emittedDepths.has(threshold)) return;
      emittedDepths.add(threshold);
      trackEvent("medical_record_depth", {
        page_name: "medical_record",
        ...basePayload,
        depth_percent: threshold,
        max_depth_percent: depth,
      });
    });
  };

  const updateDepth = () => {
    const page = overlayRoot.querySelector<HTMLElement>(".medical-page");
    const height = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      page?.scrollHeight ?? 0,
    );
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const scrollable = Math.max(height - viewport, 1);
    const depth = Math.min(100, Math.round(((window.scrollY + viewport) / scrollable) * 100));
    if (depth <= maxDepth) return;
    maxDepth = depth;
    sessionStorage.setItem(`${DEPTH_STATE_PREFIX}${context.resultId}`, String(maxDepth));
    emitDepth(maxDepth);
  };

  const onScroll = () => window.requestAnimationFrame(updateDepth);
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
  window.requestAnimationFrame(updateDepth);

  activeDepthTracker = () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    updateDepth();
    trackEvent("medical_record_exit", {
      page_name: "medical_record",
      ...basePayload,
      max_depth_percent: maxDepth,
      duration_ms: Date.now() - startedAt,
    });
  };
}

function teardownMedicalRecordAnalytics() {
  activeSectionObserver?.disconnect();
  activeSectionObserver = null;
  activeDepthTracker?.();
  activeDepthTracker = null;
}

function createImageCard(context: ResultContext, record: MedicalRecord) {
  const quote = quoteLines(record.shareQuote);
  const card = document.createElement("article");
  card.className = "medical-image-card";
  card.setAttribute("aria-hidden", "true");
  card.innerHTML = `
    <header>
      <div>
        <p>单位发病图鉴 · 门诊部</p>
        <span>${escapeHtml(context.resultId)}</span>
      </div>
      <strong>已归档</strong>
    </header>
    <section class="medical-image-identity">
      <span>确诊人格</span>
      <h2>${escapeHtml(record.personaName)}</h2>
      <p>${escapeHtml(record.personaCode)}</p>
    </section>
    ${
      context.bloodPressureSwitchText
        ? `<section class="medical-image-pressure"><span>血压开关</span><p>${escapeHtml(
            context.bloodPressureSwitchText,
          )}</p></section>`
        : ""
    }
    <section class="medical-image-symptoms">
      <span>典型症状</span>
      <ul>${[record.symptom1, record.symptom2, record.symptom3]
        .map((symptom) => `<li>${escapeHtml(symptom)}</li>`)
        .join("")}</ul>
    </section>
    ${
      quote.length
        ? `<div class="medical-image-quote">${quote
            .map((line) => `<span>${escapeHtml(line)}</span>`)
            .join("")}</div>`
        : ""
    }
    ${
      context.qrHtml
        ? `<footer>
            <div>
              <strong>${escapeHtml(record.qrTitle)}</strong>
              <p>${escapeHtml(record.qrSubtitle)}</p>
            </div>
            <div class="medical-image-qr">${context.qrHtml}</div>
          </footer>`
        : ""
    }
  `;
  document.body.append(card);
  return card;
}

async function generateRecordImage(context: ResultContext, record: MedicalRecord) {
  const button = overlayRoot.querySelector<HTMLButtonElement>("[data-generate-record]");
  if (button) {
    button.disabled = true;
    button.textContent = "正在生成病历图…";
  }
  const card = createImageCard(context, record);
  try {
    const imageUrl = await toPng(card, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#f8f5ef",
    });
    showImagePreview(imageUrl, context, record);
    trackEvent("medical_record_image_generate", {
      page_name: "medical_record",
      result_id: context.resultId,
      persona_id: record.personaId,
      persona_name: record.personaName,
      persona_code: record.personaCode,
      blood_pressure_switch_text: context.bloodPressureSwitchText,
    });
  } catch (error) {
    warn("病历图生成失败。", error);
  } finally {
    card.remove();
    if (button) {
      button.disabled = false;
      button.textContent = "生成我的单位病历图";
    }
  }
}

function showImagePreview(imageUrl: string, context: ResultContext, record: MedicalRecord) {
  const modal = document.createElement("div");
  modal.className = "medical-image-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "单位病历图预览");
  modal.innerHTML = `
    <div class="medical-image-preview">
      <div class="medical-preview-copy">
        <strong>单位病历图已生成</strong>
        <span>长按图片保存，或点击下载。</span>
      </div>
      <img src="${imageUrl}" alt="${escapeHtml(record.personaName)}单位病历图">
      <div class="medical-preview-actions">
        <a download="单位发病图鉴-${escapeHtml(context.resultId)}.png" href="${imageUrl}">下载病历图</a>
        <button type="button">关闭</button>
      </div>
    </div>
  `;
  modal.querySelector("button")?.addEventListener("click", () => modal.remove());
  modal.querySelector("a")?.addEventListener("click", () => {
    trackEvent("medical_record_image_download", {
      page_name: "medical_record",
      result_id: context.resultId,
      persona_id: record.personaId,
      persona_name: record.personaName,
      persona_code: record.personaCode,
    });
  });
  modal.addEventListener("click", (event) => {
    if (event.target === modal) modal.remove();
  });
  document.body.append(modal);
}

async function shareRecord(context: ResultContext, record: MedicalRecord) {
  const shareText = [
    `我的单位病历：${record.personaName}`,
    cleanShareQuote(record.shareQuote) ?? record.chiefComplaint,
    "来测测你的单位病症。",
  ].join("\n");
  try {
    if (navigator.share) {
      await navigator.share({ title: "单位发病图鉴", text: shareText, url: location.href });
    } else {
      await navigator.clipboard.writeText(shareText);
      showToast("会诊文案已复制");
    }
    trackEvent("medical_record_share", {
      page_name: "medical_record",
      result_id: context.resultId,
      persona_id: record.personaId,
      persona_name: record.personaName,
      persona_code: record.personaCode,
      blood_pressure_switch_text: context.bloodPressureSwitchText,
    });
  } catch (error) {
    warn("分享病历未完成。", error);
  }
}

function showToast(message: string) {
  const toast = document.createElement("div");
  toast.className = "medical-toast";
  toast.textContent = message;
  document.body.append(toast);
  requestAnimationFrame(() => toast.classList.add("visible"));
  window.setTimeout(() => toast.remove(), 1800);
}

function restoreHashState() {
  if (location.hash !== MEDICAL_HASH) {
    if (!overlayRoot.hidden) {
      teardownMedicalRecordAnalytics();
      overlayRoot.hidden = true;
      overlayRoot.replaceChildren();
      if (root) root.hidden = false;
      document.body.classList.remove("medical-record-open");
    }
    return;
  }
  const context = loadContext();
  if (!context?.resultId) {
    warn("无法恢复病历页：未找到 result_id。");
    history.replaceState(null, "", `${location.pathname}${location.search}`);
    return;
  }
  const record =
    medicalRecordByPersonaCode.get(context.personaCode) ??
    medicalRecordByPersonaName.get(context.personaName);
  if (!record) {
    warn("无法恢复病历页：未找到人格病历。", context);
    history.replaceState(null, "", `${location.pathname}${location.search}`);
    return;
  }
  renderMedicalRecord(context, record);
}

const observer = new MutationObserver(() => {
  window.requestAnimationFrame(enhanceResultPage);
});
if (root) observer.observe(root, { childList: true, subtree: true });
window.addEventListener("popstate", restoreHashState);
window.addEventListener("hashchange", restoreHashState);
window.requestAnimationFrame(() => {
  enhanceResultPage();
  restoreHashState();
});
